import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardHeader } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Loading } from '../../components/common/Loading';
import { Avatar } from '../../components/common/Avatar';
import { getCohort, removeCohortMember } from '../../api/cohorts';
import { listDatumByPrincipal, createDatum, deleteDatum, DATUM_KINDS } from '../../api/datum';
import type { Datum } from '../../api/datum';
import { ROUTES } from '../../utils/constants';
import type { Cohort, CohortMember } from '../../types/messaging';
import { FormView } from '../../components/forms/FormView';

interface CohortDetailProps {
  tenant: string;
}

export function CohortDetail({ tenant }: CohortDetailProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [cohort, setCohort] = useState<Cohort | null>(null);
  const [members, setMembers] = useState<CohortMember[]>([]);
  const [datum, setDatum] = useState<Datum[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());

  // Signal creation state
  const [showDatumMenu, setShowDatumMenu] = useState(false);
  const [isCreatingDatum, setIsCreatingDatum] = useState(false);

  // Collapsible sections state
  const [isMembersCollapsed, setIsMembersCollapsed] = useState(false);
  const [isPostsCollapsed, setIsPostsCollapsed] = useState(false);

  // Replies modal state
  const [selectedDatumForReplies, setSelectedDatumForReplies] = useState<Datum | null>(null);
  const [showRepliesModal, setShowRepliesModal] = useState(false);

  const loadData = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const [cohortData, signalsData] = await Promise.all([
        getCohort(tenant, id),
        listDatumByPrincipal(tenant, id),
      ]);
      setCohort(cohortData.cohort);
      setMembers(cohortData.members);
      setDatum(signalsData.signals);
    } catch (err) {
      console.error('Failed to load cohort:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [tenant, id]);

  const handleSelectAll = () => {
    if (selectedMembers.size === members.length) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(members.map((m) => m.userId)));
    }
  };

  const handleSelectMember = (userId: string) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedMembers(newSelected);
  };

  const handleRemoveMembers = async () => {
    if (!id || selectedMembers.size === 0) return;

    try {
      for (const userId of selectedMembers) {
        await removeCohortMember(tenant, id, userId);
      }
      setSelectedMembers(new Set());
      loadData();
    } catch (err) {
      console.error('Failed to remove members:', err);
    }
  };

  const handleCreateDatum = async (kind: string) => {
    if (!id) return;

    setShowDatumMenu(false);

    // Navigate to form editor for form-based widgets
    if (kind === 'reminder' || kind === 'checklist' || kind === 'form' || kind === 'poll' || kind === 'goal' || kind === 'habit') {
      navigate(`${ROUTES.coach.createForm(tenant, id)}?kind=${kind}`);
      return;
    }

    setIsCreatingDatum(true);

    try {
      await createDatum(tenant, {
        kind,
        principalId: id,
        accessMode: 'read_write',
        fixedData: {},
      });
      loadData();
    } catch (err) {
      console.error('Failed to create signal:', err);
    } finally {
      setIsCreatingDatum(false);
    }
  };

  const handleEditDatum = (signal: Datum) => {
    if (!id) return;

    // Navigate to form editor for form-based widgets
    if (signal.kind === 'reminder' || signal.kind === 'checklist' || signal.kind === 'form' || signal.kind === 'poll' || signal.kind === 'goal' || signal.kind === 'habit') {
      navigate(ROUTES.coach.editForm(tenant, id, signal.id));
      return;
    }

    // TODO: Add edit pages for other signal types
  };

  const handleDeleteDatum = async (signalId: string) => {
    try {
      await deleteDatum(tenant, signalId);
      loadData();
    } catch (err) {
      console.error('Failed to delete signal:', err);
    }
  };

  // Count unique users who have replied to a datum
  const getReplyCount = (signal: Datum): number => {
    if (!signal.datumUsers) return 0;
    const uniqueUserIds = new Set(signal.datumUsers.map((du: any) => du.userId));
    return uniqueUserIds.size;
  };

  // Get users who haven't replied to a datum
  const getUsersWhoDidNotReply = (signal: Datum): CohortMember[] => {
    if (!signal.datumUsers) return members;
    const repliedUserIds = new Set(signal.datumUsers.map((du: any) => du.userId));
    return members.filter(member => !repliedUserIds.has(member.userId));
  };

  // Handle nudge (send DM to users who haven't replied)
  const handleNudge = async (signal: Datum) => {
    const usersToNudge = getUsersWhoDidNotReply(signal);
    if (usersToNudge.length === 0) {
      alert('All members have already replied!');
      return;
    }

    const confirmation = confirm(
      `Send a reminder to ${usersToNudge.length} member${usersToNudge.length > 1 ? 's' : ''} who haven't replied?`
    );

    if (!confirmation) return;

    // TODO: Implement DM sending
    console.log('Nudging users:', usersToNudge.map(u => u.name).join(', '));
    alert(`Nudge feature coming soon! Would send DMs to ${usersToNudge.length} members.`);
  };

  if (isLoading) {
    return <Loading message="Loading cohort..." />;
  }

  if (!cohort) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Cohort not found</p>
        <Link to={ROUTES.coach.dashboard(tenant)}>
          <Button variant="outline" className="mt-4">
            Back to Dashboard
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={ROUTES.coach.dashboard(tenant)}>
            <Button variant="ghost" size="sm">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{cohort.name}</h1>
            <p className="text-sm text-gray-500">{members.length} members</p>
          </div>
        </div>
      </div>

      {/* Selection Actions */}
      {selectedMembers.size > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-blue-800">
              {selectedMembers.size} member{selectedMembers.size > 1 ? 's' : ''} selected
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedMembers(new Set())}
              >
                Clear Selection
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={handleRemoveMembers}
              >
                Remove from Cohort
              </Button>
              <Button variant="primary" size="sm" disabled>
                Send Message
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Members List */}
      <Card>
        <div
          className="px-6 py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
          onClick={() => setIsMembersCollapsed(!isMembersCollapsed)}
        >
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Members</h3>
            <p className="text-sm text-gray-500">Users in this cohort</p>
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isMembersCollapsed ? '' : 'rotate-180'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {!isMembersCollapsed && (
          <div>

        {members.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <p className="mt-2">No members in this cohort yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Go to Users page to add members
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedMembers.size === members.length && members.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role in Cohort
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {members.map((member) => (
                  <tr key={member.userId} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedMembers.has(member.userId)}
                        onChange={() => handleSelectMember(member.userId)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <Avatar src={member.avatarUrl} name={member.name || 'Unknown'} size="sm" />
                        <div>
                          <p className="font-medium text-gray-900">{member.name || 'Unknown'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${member.role === 'coach'
                          ? 'bg-blue-100 text-blue-800'
                          : member.role === 'moderator'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-800'
                          }`}
                      >
                        {member.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <Link to={ROUTES.coach.userDetail(tenant, member.userId)}>
                        <Button variant="ghost" size="sm">
                          View Profile
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
        )}
      </Card>

      {/* Signals */}
      <Card>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div
            className="flex-1 cursor-pointer hover:opacity-75"
            onClick={() => setIsPostsCollapsed(!isPostsCollapsed)}
          >
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">Posts</h3>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${isPostsCollapsed ? '' : 'rotate-180'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            <p className="text-sm text-gray-500">Posts in this cohort</p>
          </div>
          <div>
            <div className="relative">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowDatumMenu(!showDatumMenu)}
                isLoading={isCreatingDatum}
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add
              </Button>

              {/* Signal type menu */}
              {showDatumMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowDatumMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                    <div className="py-1">
                      <button
                        onClick={() => handleCreateDatum(DATUM_KINDS.EQUATION)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <div className="font-medium">Equation</div>
                        <div className="text-xs text-gray-500">Math formula widget</div>
                      </button>
                      <button
                        onClick={() => handleCreateDatum(DATUM_KINDS.REFLECTION)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <div className="font-medium">Reflection</div>
                        <div className="text-xs text-gray-500">Guided reflection prompt</div>
                      </button>
                      <button
                        onClick={() => handleCreateDatum(DATUM_KINDS.REMINDER)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <div className="font-medium">Reminder</div>
                        <div className="text-xs text-gray-500">Important notice or reminder</div>
                      </button>
                      <button
                        onClick={() => handleCreateDatum(DATUM_KINDS.GOAL)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <div className="font-medium">Goal</div>
                        <div className="text-xs text-gray-500">Goal tracking</div>
                      </button>
                      <button
                        onClick={() => handleCreateDatum(DATUM_KINDS.HABIT)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <div className="font-medium">Habit</div>
                        <div className="text-xs text-gray-500">Daily habit tracker</div>
                      </button>
                      <button
                        onClick={() => handleCreateDatum(DATUM_KINDS.CHECKLIST)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <div className="font-medium">Checklist</div>
                        <div className="text-xs text-gray-500">Task checklist</div>
                      </button>
                      <button
                        onClick={() => handleCreateDatum(DATUM_KINDS.POLL)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <div className="font-medium">Poll</div>
                        <div className="text-xs text-gray-500">Survey or poll</div>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {!isPostsCollapsed && (
          <div>
            {datum.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">No signals yet. Add a signal to enable widgets.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {datum.map((signal) => {
              const replyCount = getReplyCount(signal);
              const formBasedKinds = ['form', 'reminder', 'checklist', 'poll', 'goal', 'habit'];
              const isFormBased = formBasedKinds.includes(signal.kind);

              return (
                <div key={signal.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Header */}
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs text-gray-500">
                        Created {new Date(signal.createdAt).toLocaleDateString()}
                      </span>

                      {replyCount > 0 ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          {replyCount} {replyCount === 1 ? 'user' : 'users'} replied
                        </span>
                      ) : null}

                      {getUsersWhoDidNotReply(signal).length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleNudge(signal)}
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                          </svg>
                          Nudge ({getUsersWhoDidNotReply(signal).length})
                        </Button>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditDatum(signal)}
                        disabled={!isFormBased}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteDatum(signal.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>

                  {/* Widget content */}
                  <div className="p-4">
                    {isFormBased ? (
                      <FormView
                        markdown={(signal.fixedData?.body as string) || ''}
                        formData={{}}
                        onChange={() => {}} // Read-only, no-op
                        readOnly={true}
                        showProgress={false}
                        showSaveButton={false}
                      />
                    ) : (
                      <div className="space-y-2">
                        <div className="text-sm text-gray-600">
                          <span className="font-medium capitalize">{signal.kind}</span> widget
                        </div>
                        {signal.fixedData && Object.keys(signal.fixedData).length > 0 && (
                          <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto">
                            {JSON.stringify(signal.fixedData, null, 2)}
                          </pre>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Replies button */}
                  {replyCount > 0 && (
                    <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedDatumForReplies(signal);
                          setShowRepliesModal(true);
                        }}
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                        View Replies ({replyCount})
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </div>
        )}
      </Card>

      {/* Replies Modal */}
      {showRepliesModal && selectedDatumForReplies && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => setShowRepliesModal(false)}
            />

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              {/* Header */}
              <div className="bg-white px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Replies ({getReplyCount(selectedDatumForReplies)})
                </h3>
                <button
                  onClick={() => setShowRepliesModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Replies list */}
              <div className="bg-gray-50 px-6 py-4 max-h-[70vh] overflow-y-auto">
                <div className="space-y-6">
                  {selectedDatumForReplies.datumUsers?.map((datumUser: any) => {
                    const member = members.find(m => m.userId === datumUser.userId);
                    return (
                      <div key={datumUser.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        {/* User header */}
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
                          <Avatar src={member?.avatarUrl} name={member?.name || 'Unknown'} size="sm" />
                          <div>
                            <p className="font-medium text-gray-900">{member?.name || 'Unknown'}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(datumUser.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </div>

                        {/* Form response */}
                        <div className="p-4">
                          <FormView
                            markdown={(selectedDatumForReplies.fixedData?.body as string) || ''}
                            formData={datumUser.state || {}}
                            onChange={() => {}} // Read-only
                            readOnly={true}
                            showProgress={false}
                            showSaveButton={false}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Footer */}
              <div className="bg-white px-6 py-4 border-t border-gray-200 flex justify-end">
                <Button variant="outline" onClick={() => setShowRepliesModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
